/* eslint-disable react-hooks/exhaustive-deps */
import { DASHBOARD_TYPES, HUB_ROLES } from 'constants/global';
import ROUTES from 'constants/routes';
import { collection, onSnapshot, orderBy, query, where, Timestamp } from 'firebase/firestore';
import useUser from 'hooks/useUser';
import React, { useRef, useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import { setPendingTrades } from 'redux/slices/app';
import { db, fetchWithInQuery, getFromStorage, saveToStorage } from 'utils';
import { formatDateValues } from 'utils/formatDateValues';
import { convertToSlug } from 'utils/slug';

const SideBar = ({ sideBarOpen, mobileScreen, setSideBarOpen }) => {
    const dashboardType = getFromStorage('ams-dashboard-type');
    const { pendingTrades, currentHub } = useSelector((state) => state.app);
    const { hasAllAccess, isTrade, isWholeSale, isSuperAdmin, user, isWholeSaleManager, isFinance } = useUser();
    const [showCover, setShowCover] = useState(false);
    const [hubDeliveriesCount, setHubDeliveriesCount] = useState(null);
    const [externalDeliveriesCount, setExternalDeliveriesCount] = useState(null);
    const [onlineDeliveriesCount, setOnlineDeliveriesCount] = useState(null);
    const [requisitionsCount, setRequisitionsCount] = useState(null);
    const [logisticsExpensesCount, setLogisticsExpensesCount] = useState(null);
    const [routePlansCount, setRoutePlansCount] = useState(null);
    const [unseenExpensesCount, setUnseenExpensesCount] = useState(null);
    const [maintenanceCount, setMaintenanceCount] = useState(null);
    const [hubInventoryCount, setHubInventoryCount] = useState(null);
    const [hubDeliveriesUnseenCount, setHubDeliveriesUnseenCount] = useState(null);
    const [hubUnfulfilledSalesCount, setHubUnfulfilledSalesCount] = useState(null);
    const timeout = useRef();
    const { hub } = useParams();
    const dispatch = useDispatch();
    const hubUser = user?.role === HUB_ROLES.AGENT || user?.role === HUB_ROLES.MANAGER;

    const deliveryRequestsCount =
        hubDeliveriesCount !== null || externalDeliveriesCount !== null || onlineDeliveriesCount !== null
            ? (hubDeliveriesCount ?? 0) + (externalDeliveriesCount ?? 0) + (onlineDeliveriesCount ?? 0)
            : null;
    const financeRequestsCount =
        requisitionsCount !== null || logisticsExpensesCount !== null
            ? (requisitionsCount ?? 0) + (logisticsExpensesCount ?? 0)
            : null;

    const closeBar = () => {
        setShowCover(false);
        mobileScreen && setSideBarOpen(false);
    };

    useEffect(() => {
        if (sideBarOpen && mobileScreen) {
            timeout.current = setTimeout(() => {
                setShowCover(true);
            }, 30);
        } else {
            setShowCover(false);
            timeout.current && clearTimeout(timeout.current);
        }
    }, [sideBarOpen, mobileScreen]);

    useEffect(() => {
        if (isSuperAdmin) {
            const unSubAutoPendingTrade = onSnapshot(
                query(collection(db, 'pending-trades'), orderBy('createdAt', 'desc')),
                (querySnapshot) => {
                    const trades = [];
                    querySnapshot.forEach((doc) => {
                        trades.push({
                            ...doc.data(),
                            ...formatDateValues(doc.data()),
                        });
                    });
                    dispatch(setPendingTrades(trades));
                }
            );

            return () => {
                unSubAutoPendingTrade();
            };
        }
    }, [isSuperAdmin]);

    useEffect(() => {
        if (hasAllAccess) {
            const unSubRequisitionsRequests = onSnapshot(
                query(collection(db, 'requisitions'), where('status', '==', 'pending')),
                (querySnapshot) => {
                    setRequisitionsCount(querySnapshot.size);
                }
            );

            const unSubLogisticsExpensesRequests = onSnapshot(
                query(collection(db, 'logisticsExpenses'), where('status', '==', 'pending')),
                (querySnapshot) => {
                    setLogisticsExpensesCount(querySnapshot.size);
                }
            );

            return () => {
                unSubRequisitionsRequests();
                unSubLogisticsExpensesRequests();
            };
        }
    }, [hasAllAccess]);

    useEffect(() => {
        if (dashboardType === DASHBOARD_TYPES.LOGISTICS) {
            const unSubHubDeliveries = onSnapshot(
                query(collection(db, 'hubDeliveries'), where('status', '==', 'pending')),
                (querySnapshot) => {
                    setHubDeliveriesCount(querySnapshot.size);
                }
            );

            const unSubExternalDeliveries = onSnapshot(
                query(collection(db, 'externalDeliveries'), where('status', '==', 'pending')),
                (querySnapshot) => {
                    setExternalDeliveriesCount(querySnapshot.size);
                }
            );

            const unSubOnlineDeliveries = onSnapshot(
                query(collection(db, 'onlineDeliveries'), where('status', '==', 'pending')),
                (querySnapshot) => {
                    setOnlineDeliveriesCount(querySnapshot.size);
                }
            );

            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);

            const unSubRoutePlans = onSnapshot(
                query(
                    collection(db, 'routePlans'),
                    where('createdAt', '>=', Timestamp.fromDate(startOfToday)),
                    where('createdAt', '<=', Timestamp.fromDate(endOfToday))
                ),
                (querySnapshot) => {
                    setRoutePlansCount(querySnapshot.size);
                }
            );

            let allExpenseDocs = [];

            const calculateUnseenCount = () => {
                const seenExpenseIds = getFromStorage('seen-expense-ids') || [];
                const unseenCount = allExpenseDocs.filter((doc) => {
                    return !seenExpenseIds.includes(doc.id);
                }).length;
                setUnseenExpensesCount(unseenCount);
            };

            const unSubUnseenExpenses = onSnapshot(
                query(collection(db, 'logisticsExpenses'), where('status', 'in', ['approved', 'rejected'])),
                (querySnapshot) => {
                    allExpenseDocs = querySnapshot.docs;
                    calculateUnseenCount();
                }
            );

            const handleExpensesVisitedInEffect = () => {
                calculateUnseenCount();
            };

            window.addEventListener('expenses-visited', handleExpensesVisitedInEffect);

            // Fetch all vehicles and check their maintenance status
            const maintenanceUnsubscribers = [];
            const vehicleMaintenanceCounts = {};

            const updateTotalMaintenanceCount = () => {
                const total = Object.values(vehicleMaintenanceCounts).reduce((sum, count) => sum + count, 0);
                setMaintenanceCount(total);
            };

            const setupMaintenanceListeners = (assets) => {
                // Clean up old listeners
                maintenanceUnsubscribers.forEach((unsub) => unsub());
                maintenanceUnsubscribers.length = 0;

                assets.forEach((asset) => {
                    const vehicleTag = asset.data().vehicleTag;
                    const maintenanceQuery = query(
                        collection(db, `maintenance/${vehicleTag}/${vehicleTag}`),
                        where('maintenanceDate', '==', null)
                    );

                    const unsubscribe = onSnapshot(maintenanceQuery, (snapshot) => {
                        const needsService = snapshot.docs.filter((doc) => {
                            const data = doc.data();
                            return data.mileage >= data.nextMaintenanceMileage;
                        }).length;

                        vehicleMaintenanceCounts[vehicleTag] = needsService;
                        updateTotalMaintenanceCount();
                    });

                    maintenanceUnsubscribers.push(unsubscribe);
                });
            };

            const unSubMaintenanceCount = onSnapshot(collection(db, 'companyAssets'), (querySnapshot) => {
                setupMaintenanceListeners(querySnapshot.docs);
            });

            return () => {
                unSubHubDeliveries();
                unSubExternalDeliveries();
                unSubOnlineDeliveries();
                unSubRoutePlans();
                unSubUnseenExpenses();
                unSubMaintenanceCount();
                maintenanceUnsubscribers.forEach((unsub) => unsub());
                window.removeEventListener('expenses-visited', handleExpensesVisitedInEffect);
            };
        }
    }, [dashboardType]);

    useEffect(() => {
        if (hubUser && currentHub) {
            const isSaturday =
                new Date().toLocaleDateString('en-US', {
                    timeZone: 'Africa/Lagos',
                    weekday: 'long',
                }) === 'Saturday';

            if (!isSaturday) {
                setHubInventoryCount(0);
                return;
            }

            const startDateTimestamp = new Date();
            startDateTimestamp.setHours(0, 0, 0, 0);

            const endDateTimestamp = new Date();
            endDateTimestamp.setHours(23, 59, 59, 999);

            const tradeCollectionPath = `hubs/${currentHub.id}/trade-inventory`;
            const storageCollectionPath = `hubs/${currentHub.id}/storage-inventory`;

            let availableTradeCodes = [];
            let availableStorageCodes = [];
            let addedTradeCodes = [];
            let addedStorageCodes = [];

            const calculatePendingCount = () => {
                const tradeCodesWithoutInventory = availableTradeCodes.filter(
                    (code) => !addedTradeCodes.includes(code)
                );
                const storageCodesWithoutInventory = availableStorageCodes.filter(
                    (code) => !addedStorageCodes.includes(code)
                );

                const pendingCount = tradeCodesWithoutInventory.length + storageCodesWithoutInventory.length;
                setHubInventoryCount(pendingCount > 0 ? pendingCount : 0);
            };

            // Fetch available trade codes (trades that match hub's tradeCodes)
            if (currentHub.tradeCodes?.length) {
                fetchWithInQuery('trades', 'code', currentHub.tradeCodes, (dataArr) => {
                    availableTradeCodes = dataArr.map((data) => data.code);
                    calculatePendingCount();
                });
            }

            // Fetch available storage codes (storage that match hub's storageCodes)
            if (currentHub.storageCodes?.length) {
                fetchWithInQuery('storage', 'code', currentHub.storageCodes, (dataArr) => {
                    availableStorageCodes = dataArr.map((data) => data.code);
                    calculatePendingCount();
                });
            }

            const unSubTradeInventory = onSnapshot(
                query(
                    collection(db, tradeCollectionPath),
                    where('createdAt', '>=', Timestamp.fromDate(startDateTimestamp)),
                    where('createdAt', '<=', Timestamp.fromDate(endDateTimestamp))
                ),
                (tradeSnapshot) => {
                    addedTradeCodes = tradeSnapshot.docs.map((doc) => doc.data().code);
                    calculatePendingCount();
                }
            );

            const unSubStorageInventory = onSnapshot(
                query(
                    collection(db, storageCollectionPath),
                    where('createdAt', '>=', Timestamp.fromDate(startDateTimestamp)),
                    where('createdAt', '<=', Timestamp.fromDate(endDateTimestamp))
                ),
                (storageSnapshot) => {
                    addedStorageCodes = storageSnapshot.docs.map((doc) => doc.data().code);
                    calculatePendingCount();
                }
            );

            return () => {
                unSubTradeInventory();
                unSubStorageInventory();
            };
        }
    }, [hubUser, currentHub]);

    useEffect(() => {
        if (hubUser && currentHub) {
            let allDeliveryDocs = [];

            const calculateUnseenCount = () => {
                let seenDeliveryData = getFromStorage('seen-hub-delivery-ids') || {};

                // Migrate from old array format to new object format
                if (Array.isArray(seenDeliveryData)) {
                    seenDeliveryData = {};
                    saveToStorage('seen-hub-delivery-ids', seenDeliveryData);
                }

                const unseenCount = allDeliveryDocs.filter((doc) => {
                    const data = doc.data();
                    const lastSeenStatus = seenDeliveryData[doc.id];
                    return data.status !== 'pending' && (!lastSeenStatus || lastSeenStatus !== data.status);
                }).length;
                setHubDeliveriesUnseenCount(unseenCount);
            };

            const unSubUnseenDeliveries = onSnapshot(
                query(collection(db, 'hubDeliveries'), where('hub', '==', currentHub.name)),
                (querySnapshot) => {
                    allDeliveryDocs = querySnapshot.docs;
                    calculateUnseenCount();
                }
            );

            const handleDeliveriesVisitedInEffect = () => {
                calculateUnseenCount();
            };

            window.addEventListener('hub-deliveries-visited', handleDeliveriesVisitedInEffect);

            return () => {
                unSubUnseenDeliveries();
                window.removeEventListener('hub-deliveries-visited', handleDeliveriesVisitedInEffect);
            };
        }
    }, [hubUser, currentHub]);

    useEffect(() => {
        if (hubUser && currentHub) {
            const unSubUnfulfilledSales = onSnapshot(
                query(
                    collection(db, `hubs/${currentHub.id}/sales`),
                    where('online', '==', true),
                    where('fulfilled', '==', false)
                ),
                (querySnapshot) => {
                    setHubUnfulfilledSalesCount(querySnapshot.size);
                }
            );

            return () => {
                unSubUnfulfilledSales();
            };
        }
    }, [hubUser, currentHub]);

    return (
        <>
            {showCover && (
                <div
                    className={`fixed bottom-0 left-0 h-screen z-[1000] w-screen transition-colors duration-300 ${
                        showCover && 'bg-gray-600/40 backdrop-blur-[1px]'
                    }`}
                    onClick={() => closeBar()}
                />
            )}

            <aside
                className={`transition-all bg-white duration-300 p-5 pb-8 border-r overflow-y-auto ${
                    sideBarOpen
                        ? 'max-sm:fixed max-sm:h-[calc(100vh-58px)] !left-0 !bottom-0 max-475:w-[200px] max-sm:w-[230px] max-sm:!z-[1200] px-4'
                        : 'px-[9px]'
                } ${(dashboardType !== DASHBOARD_TYPES.WHOLESALE || hubUser) && 'pt-[30px]'}`}
            >
                {hubUser ? (
                    <>
                        <Bar
                            content={'Sales'}
                            iconName={'payments'}
                            path={`/hub/${convertToSlug(hub)}/sales`}
                            open={sideBarOpen}
                            closeBar={closeBar}
                            count={hubUnfulfilledSalesCount}
                        />
                        <Bar
                            content={'Restock Hub'}
                            iconName={'contract_edit'}
                            path={`/hub/${convertToSlug(hub)}/restock`}
                            open={sideBarOpen}
                            closeBar={closeBar}
                        />
                        <Bar
                            content={'Inventory'}
                            iconName={'account_balance_wallet'}
                            path={`/hub/${convertToSlug(hub)}/inventory`}
                            open={sideBarOpen}
                            closeBar={closeBar}
                            count={hubInventoryCount}
                        />

                        <Bar
                            content={'Deliveries'}
                            iconName={'delivery_truck_speed'}
                            path={`/hub/${convertToSlug(hub)}/deliveries`}
                            open={sideBarOpen}
                            closeBar={closeBar}
                            count={hubDeliveriesUnseenCount}
                        />
                    </>
                ) : (
                    <>
                        {dashboardType === DASHBOARD_TYPES.WHOLESALE ? (
                            <p
                                className={`${
                                    sideBarOpen
                                        ? 'text-[11px] pb-4 font-semibold text-text pt-4'
                                        : 'font-medium text-2xl text-center pb-3 text-text-light'
                                }`}
                            >
                                {sideBarOpen ? 'GENERAL' : '...'}
                            </p>
                        ) : (
                            ''
                        )}
                        <Bar
                            content={'Dashboard'}
                            iconName={'house'}
                            path={ROUTES.HOMEPAGE}
                            open={sideBarOpen}
                            closeBar={closeBar}
                        />

                        {dashboardType === DASHBOARD_TYPES.WHOLESALE ? (
                            <>
                                {user?.role && (
                                    <>
                                        {(isTrade || hasAllAccess || isWholeSaleManager) && (
                                            <Bar
                                                content={'Trades'}
                                                iconName={'add_business'}
                                                path={ROUTES.TRADES}
                                                open={sideBarOpen}
                                                closeBar={closeBar}
                                                count={
                                                    isSuperAdmin
                                                        ? pendingTrades?.filter(({ rejected }) => !rejected).length
                                                        : undefined
                                                }
                                            />
                                        )}
                                        {(isWholeSale || hasAllAccess || isWholeSaleManager) && (
                                            <Bar
                                                content={'Sales'}
                                                iconName={'payments'}
                                                path={ROUTES.SALES}
                                                open={sideBarOpen}
                                                closeBar={closeBar}
                                            />
                                        )}
                                        <Bar
                                            content={'Expenses'}
                                            iconName={'price_change'}
                                            path={ROUTES.REQUISITIONS}
                                            open={sideBarOpen}
                                            closeBar={closeBar}
                                        />

                                        {(isWholeSale || hasAllAccess || isWholeSaleManager) && (
                                            <Bar
                                                content={'Farmers'}
                                                iconName={'store'}
                                                path={ROUTES.FARMERS}
                                                open={sideBarOpen}
                                                closeBar={closeBar}
                                            />
                                        )}

                                        {(isSuperAdmin || isFinance) && (
                                            <>
                                                <Bar
                                                    content={'Storage'}
                                                    iconName={'warehouse'}
                                                    path={ROUTES.STORAGE}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                                <Bar
                                                    content={'Retail Hubs'}
                                                    iconName={'storefront'}
                                                    path={ROUTES.HUBS}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                            </>
                                        )}

                                        <Bar
                                            content={'Deliveries'}
                                            iconName={'delivery_truck_speed'}
                                            path={ROUTES.RETAIL_DELIVERIES}
                                            open={sideBarOpen}
                                            closeBar={closeBar}
                                        />

                                        {(hasAllAccess || isWholeSaleManager) && (
                                            <>
                                                <p
                                                    className={`mt-3 ${
                                                        sideBarOpen
                                                            ? 'text-[11px] pb-4 font-semibold text-text pt-4'
                                                            : 'font-medium text-2xl text-center pb-3 text-text-light'
                                                    }`}
                                                >
                                                    {sideBarOpen ? 'METRICS' : '...'}
                                                </p>
                                                <Bar
                                                    content={'Product CM2'}
                                                    iconName={'add_notes'}
                                                    path={ROUTES.CONTRIBUTION_MARGIN}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                                <Bar
                                                    content={'Location CM2'}
                                                    iconName={'file_map'}
                                                    path={ROUTES.LOCATIONS_CM2}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                                <Bar
                                                    content={'Hub Sales'}
                                                    iconName={'trending_up'}
                                                    path={ROUTES.HUBS_MONTHLY_SALES}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                                <Bar
                                                    content={'Inventory'}
                                                    iconName={'account_balance_wallet'}
                                                    path={ROUTES.INVENTORY}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                                <Bar
                                                    content={'AI'}
                                                    iconName={'assistant_on_hub'}
                                                    path={ROUTES.AI}
                                                    open={sideBarOpen}
                                                    closeBar={closeBar}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                                {hasAllAccess && (
                                    <>
                                        <p
                                            className={`mt-3 ${
                                                sideBarOpen
                                                    ? 'text-[11px] pb-4 font-semibold text-text pt-4'
                                                    : 'font-medium text-2xl text-center pb-3 text-text-light'
                                            }`}
                                        >
                                            {sideBarOpen ? 'FINANCE' : '...'}
                                        </p>
                                        <Bar
                                            content={'Requests'}
                                            iconName={'request_quote'}
                                            path={ROUTES.FINANCE_REQUISITIONS}
                                            open={sideBarOpen}
                                            closeBar={closeBar}
                                            count={financeRequestsCount}
                                        />
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <Bar
                                    content={'Supply Trades'}
                                    iconName={'forklift'}
                                    path={ROUTES.SUPPLY_TRADES}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                />
                                <Bar
                                    content={'Deliveries'}
                                    iconName={'delivery_truck_speed'}
                                    path={ROUTES.DELIVERIES}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                    count={deliveryRequestsCount}
                                />
                                <Bar
                                    content={'Routes Plan'}
                                    iconName={'tactic'}
                                    path={ROUTES.ROUTE_PLANS}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                    count={routePlansCount}
                                />
                                <Bar
                                    content={'Expenses'}
                                    iconName={'price_change'}
                                    path={ROUTES.EXPENSES}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                    count={unseenExpensesCount}
                                />
                                <Bar
                                    content={'Company Assets'}
                                    iconName={'emoji_transportation'}
                                    path={ROUTES.ASSETS}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                />
                                <Bar
                                    content={'Maintenance'}
                                    iconName={'home_repair_service'}
                                    path={ROUTES.MAINTENANCE}
                                    open={sideBarOpen}
                                    closeBar={closeBar}
                                    count={maintenanceCount}
                                />

                                {(hasAllAccess || isWholeSaleManager) && (
                                    <>
                                        <p
                                            className={`mt-3 ${
                                                sideBarOpen
                                                    ? 'text-[11px] pb-4 font-semibold text-text pt-4'
                                                    : 'font-medium text-2xl text-center pb-3 text-text-light'
                                            }`}
                                        >
                                            {sideBarOpen ? 'METRICS' : '...'}
                                        </p>

                                        <Bar
                                            content={'AI'}
                                            iconName={'assistant_on_hub'}
                                            path={ROUTES.AI}
                                            open={sideBarOpen}
                                            closeBar={closeBar}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </aside>
        </>
    );
};

const Bar = ({ path, content, iconName, open, closeBar, count }) => {
    const location = useLocation();
    const isOnPage = location.pathname === path;
    const hasCount = typeof count === 'number' && count > 0;

    return (
        <Link
            className={`flex items-center rounded-md px-2.5 py-2.5 transition-colors duration-200 group mb-1 ${
                isOnPage ? 'bg-primary/80 pointer-events-none' : 'hover:bg-[#e6ffe8]'
            }`}
            to={path}
            onClick={closeBar}
            title={open ? '' : content}
        >
            <span
                className={`material-symbols-outlined ${
                    isOnPage ? 'text-white' : 'text-text/60 group-hover:text-primary'
                } ${open ? 'text-[23px]' : 'transition-all text-[28px] mx-auto'}`}
            >
                {iconName}
            </span>

            {open && (
                <span
                    className={`ml-3 mr-4 text-[14px] ${
                        isOnPage ? 'text-white' : 'text-text/80 group-hover:text-primary'
                    }`}
                >
                    {content}
                </span>
            )}

            {hasCount && (
                <>
                    {open ? (
                        <span
                            className={`ml-auto text-xs font-bold translate-x-0.5 ${
                                isOnPage ? 'text-white' : 'text-primary'
                            }`}
                        >
                            {count}
                        </span>
                    ) : (
                        <span
                            className={` ${
                                isOnPage ? 'bg-white' : 'bg-primary'
                            } h-[7px] w-[7px] rounded-full translate-x-1.5 -translate-y-3.5 `}
                        />
                    )}
                </>
            )}
        </Link>
    );
};

export default SideBar;
